export const CONSIGNMENT_RULE_TYPES = {
  LINE_DIRECT: 'LINE_DIRECT',
  FIXED_QUANTITY: 'FIXED_QUANTITY',
  WEIGHT_FORMULA: 'WEIGHT_FORMULA',
};

export const CONSIGNMENT_RULE_TYPE_LABEL = {
  LINE_DIRECT: 'Quantité ligne',
  FIXED_QUANTITY: 'Quantité fixe',
  WEIGHT_FORMULA: 'Formule poids',
};

/** Types autorisés en création (V1 — FIXED_QUANTITY bloqué). */
export const CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS = [
  CONSIGNMENT_RULE_TYPES.LINE_DIRECT,
  CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA,
].map((value) => ({
  value,
  name: CONSIGNMENT_RULE_TYPE_LABEL[value],
}));

export const CONSIGNMENT_WEIGHT_UNIT_LABEL = {
  t: 'Tonnes',
  kg: 'Kilogrammes',
  unit: 'Unité',
};

export const CONSIGNMENT_WEIGHT_UNIT_OPTIONS = Object.entries(
  CONSIGNMENT_WEIGHT_UNIT_LABEL
).map(([value, name]) => ({ value, name }));

export function getConsignmentRuleTypeOptions(
  currentRuleType,
  mode = 'create'
) {
  if (mode === 'create') {
    return CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS;
  }

  if (currentRuleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY) {
    return [
      {
        value: CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY,
        name: CONSIGNMENT_RULE_TYPE_LABEL.FIXED_QUANTITY,
      },
      ...CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS,
    ];
  }

  return CONSIGNMENT_RULE_TYPE_CREATE_OPTIONS;
}

export function formatRuleNumber(value) {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return String(parseFloat(num.toFixed(4)));
}

export const CONSIGNMENT_WEIGHT_UNIT_SHORT = {
  t: 't',
  kg: 'kg',
  unit: 'unit',
};

export function formatWeightUnitShort(weightUnit) {
  if (!weightUnit) return 'kg';
  const normalized = String(weightUnit).toLowerCase();
  return CONSIGNMENT_WEIGHT_UNIT_SHORT[normalized] || normalized;
}

export function formatConsignmentRuleParams(rule) {
  if (!rule?.rule_type) return '-';

  switch (rule.rule_type) {
    case CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY: {
      if (rule.fixed_quantity == null) return '-';
      const qty = formatRuleNumber(rule.fixed_quantity);
      return qty != null ? `× ${qty}` : '-';
    }
    case CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA: {
      if (rule.weight_divisor == null) return '-';
      const divisor = formatRuleNumber(rule.weight_divisor);
      const unit = formatWeightUnitShort(rule.weight_unit);
      return `Poids / ${divisor} ${unit}`;
    }
    default:
      return '1 palette / unité ligne';
  }
}

export function normalizeConsignmentRule(item) {
  return {
    ...item,
    id: item.rule_id ?? item.id,
    is_active: item.is_active === 1 || item.is_active === true,
  };
}

/**
 * Adapte la réponse paginée API → format attendu par AgrivitechPagination.
 * Gère le format direct { total, page, limit, data } ou enveloppé { data: { ... } }.
 */
export function adaptConsignmentRulesListResponse(responseData) {
  const payload =
    responseData?.data?.data != null ? responseData.data : responseData;
  const { total = 0, page = 1, limit = 20, data = [] } = payload ?? {};

  return {
    pagination_info: {
      current_items_count: data.length,
      items_per_page: limit,
      total_items: total,
      current_page: page,
      total_pages: Math.max(1, Math.ceil(total / limit)),
    },
    items: data.map(normalizeConsignmentRule),
  };
}

export function getConsignmentRuleApiErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Une erreur est survenue.';

  if (data.validation_errors?.length) {
    const fields = data.validation_errors
      .map((item) => `${item.field}: ${item.message}`)
      .join(', ');
    return `${data.message} (${fields})`;
  }

  return data.message || data.error || 'Une erreur est survenue.';
}
